import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
// import Modal from '../Modals/Modal'
import { WeeklyCalendar } from '../WeeklyCalendar'
import useAuthenticatedAxios from '../../auth/AuthenticatedAxios';
import { DialogComponent } from '@syncfusion/ej2-react-popups';
import ImavenLoadingScreen from '../Helpers/loading-screen';
import { Button, ButtonVariant, ButtonSize } from '../atoms/button';
import ImavenOnlineStatus from '../Helpers/imaven-online-status';
import {
  socket,
  onExpertOnlineStatusReceived, checkExpertOnlineStatus
} from '../../socket';
import { useAuth0 } from '@auth0/auth0-react';
import ExpertCardComponent from '../organisms/ExpertCard';
import FindConsultationsBox from '../organisms/FindConsultationsBox';
import ConsultationAdviceModal from '../organisms/ConsultationAdviceModal';
import useMavenApi from '../../auth/useMavenApi';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** The conversation a marketplace consultation lives in ("threadId" = the conversation _id; match both). */
const consultationKeys = (c) => [c?.conversationId, c?.threadId].filter(Boolean).map(String);

const CONSULT_STATUS = {
  ready: 'Consultation ready',
  generating: 'Being written',
  locked: 'Not unlocked yet',
  failed: 'Could not be written',
};

// New Sort Controls Styled Components
const SortControls = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
`;

const SortButton = styled.button`
  background: ${props => props.active ? 'linear-gradient(135deg, #2d5bed 0%, #5b61ea 100%)' : 'linear-gradient(135deg, #ffffff 0%, #f8faff 100%)'};
  color: ${props => props.active ? '#ffffff' : 'rgba(255, 255, 255, 0.95)'};
  padding: 8px 16px;
  border: 1px solid ${props => props.active ? '#cad5e9' : '#e0e8f5'};
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s;
  font-size: 0.9em;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    background: ${props => props.active ? 'linear-gradient(135deg, #5b61ea 0%, #4a7aa7 100%)' : '#e6ecf6'};
    border-color: #cad5e9;
  }
`;
// Styled Components
const ExpertBoardContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 40px;
  background: linear-gradient(180deg, #f9fbff 0%, #f5f8fd 100%);
  width: 100%;
  height: 100%;
  max-width: 100%;
  margin: 0 auto;
  border-radius: 6px;
  overflow-x: hidden; // Prevent horizontal scrolling
  overflow-y: auto;
  
  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }
  
  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
`;

const SectionTitle = styled.div`
  font-size: 1em;
  color: #192947;
  font-weight: bold;
  background: linear-gradient(135deg, #ffffff 0%, #f8faff 100%);
  border: 1px solid #e0e8f5;
  padding: 8px 16px;
  border-radius: 10px;
  display: inline-block;
  margin-bottom: 16px;
  width: 12%;
`;

const ExpertCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 24px;
  width: 100%;
`;

/* Marketplace (imavenai.com) consultations folded into Home: a discreet "find" link, the claim box,
   a success banner, and the few consultations that have no conversation card (yet). */
const HomeTopRow = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: -8px;
`;

const QuietLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: #2c64da;
  font-size: 0.875em;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
`;

const HomeBanner = styled.div`
  padding: 10px 14px;
  border-radius: 10px;
  font-size: 0.9em;
  color: #0f8f63;
  background: rgba(18, 165, 113, 0.1);
  border: 1px solid rgba(18, 165, 113, 0.3);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const EmptyIntro = styled.div`
  max-width: 560px;
  font-size: 0.95em;
  line-height: 1.6;
  color: #455776;
`;

const LooseCard = styled.div`
  background: #ffffff;
  border: 1px solid #e0e8f5;
  border-radius: 12px;
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  color: #192947;

  .name { font-weight: 600; }
  .meta { font-size: 0.82em; color: #7b8aa3; }
  .need { font-size: 0.9em; color: #455776; font-style: italic; }
  .note { font-size: 0.85em; color: #65748d; }
`;


// Main ExpertBoard component
const ExpertBoard = () => {
  const authenticatedAxios = useAuthenticatedAxios();
  const { expertIdParam } = useParams();
  const [experts, setExperts] = useState([]);
  const [isModel, setIsModal] = useState(false);
  const [expertId, setExpertId] = useState("");
  const [expertName, setExpertName] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [socketConnected, setSocketConnected] = useState(false);
  const dialogInstance = useRef(null);
  const targetElement = useRef(null);
  const { isAuthenticated, user } = useAuth0();
  const [userListReceived, setUserListReceived] = useState(false);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 20; // Try more times before giving up

  // Marketplace consultations (Maven GET /client/consultations). The call also auto-claims the
  // consultations of a VERIFIED login email, so their conversations may be new to /api/experts.
  const callMaven = useMavenApi();
  const location = useLocation();
  const [consult, setConsult] = useState(null); // { email, emailVerified, list, failed? }
  const [showFind, setShowFind] = useState(false);
  const [claimBanner, setClaimBanner] = useState(null);
  const [reading, setReading] = useState(null); // consultation whose advice is open
  const [refetchPending, setRefetchPending] = useState(false);
  const refetchedForClaim = useRef(false);

  // Add global CSS for the awaiting status badge
  useEffect(() => {
    const style = document.createElement('style');
    style.id = 'global-awaiting-status-style';
    style.innerHTML = `
      .expert-status-badge.awaiting,
      .expert-status-badge.awaiting-meeting {
        color: #a2611c !important;
        border: 1px solid rgba(239, 155, 97, 0.3) !important;
        background-color: rgba(25, 41, 71, 0.156) !important;
        font-size: 0.8em !important;
        padding: 6px 8px !important;
      }
    `;
    
    if (!document.getElementById('global-awaiting-status-style')) {
      document.head.appendChild(style);
    }
    
    return () => {
      const existingStyle = document.getElementById('global-awaiting-status-style');
      if (existingStyle) {
        document.head.removeChild(existingStyle);
      }
    };
  }, []);

  // Basic experts data fetching. `silent` refreshes the cards without the full loading screen
  // (used after consultations were claimed).
  const fetchExperts = async ({ silent = false } = {}) => {
    try {
      if (!silent) setIsLoading(true);
      const response = await authenticatedAxios.get(`/api/experts`);

      // Add debugging logs to trace the data structure
      console.log('API Response data sample:', response.data.length > 0 ? response.data[0] : 'No data');

      // Get existing online status if available
      const existingOnlineStatus = {};
      experts.forEach(expert => {
        if (expert.isOnline) {
          existingOnlineStatus[expert.id] = true;
        }
      });

      const sortedData = [...response.data].sort(
        (a, b) => new Date(b.startTime) - new Date(a.startTime)
      ).map(expert => ({
        ...expert,
        // Preserve existing online status if we have it
        isOnline: existingOnlineStatus[expert.id] || false
      }));

      console.log('Processed experts with online status:',
        sortedData.map(e => ({ name: e.name, isOnline: e.isOnline })));

      setExperts(sortedData);


      // Request online status after loading experts
      if (socket && socket.connected) {
        const expertIds = [...new Set(sortedData.map(expert => expert.userId))];
        checkExpertOnlineStatus(expertIds);
      }

    } catch (error) {
      console.error('Error fetching experts:', error);
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  // Initial data loading
  useEffect(() => {
    fetchExperts();

  }, []);

  const loadConsultations = useCallback(async () => {
    try {
      const r = await callMaven('/client/consultations');
      setConsult({
        email: r?.email || user?.email || '',
        emailVerified: !!r?.emailVerified,
        list: Array.isArray(r?.consultations) ? r.consultations : [],
      });
    } catch (e) {
      // Home still works without Maven; the find box stays available.
      console.warn('[Home] could not load consultations:', e?.message || e);
      setConsult({ failed: true, email: user?.email || '', emailVerified: false, list: [] });
    }
  }, [callMaven, user]);

  useEffect(() => { loadConsultations(); }, [loadConsultations]);

  const expertThreadIds = useMemo(
    () => new Set(experts.map((e) => String(e.threadId || '')).filter(Boolean)),
    [experts]
  );
  const consultations = useMemo(() => consult?.list || [], [consult]);
  const unmatchedConsultations = useMemo(
    () => consultations.filter((c) => !consultationKeys(c).some((k) => expertThreadIds.has(k))),
    [consultations, expertThreadIds]
  );

  // Consultations just claimed point at conversations the adviser list did not have yet: refetch it once.
  const hasMissingConversation = unmatchedConsultations.some((c) => consultationKeys(c).length > 0);
  useEffect(() => {
    if (isLoading || !consult || consult.failed || refetchedForClaim.current) return;
    if (!hasMissingConversation) return;
    refetchedForClaim.current = true;
    setRefetchPending(true);
    Promise.resolve(fetchExperts({ silent: true })).finally(() => setRefetchPending(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, consult, hasMissingConversation]);
  // Loose consultation entries wait for that refetch, so a card does not flash in and out.
  const looseSettled = !refetchPending && (refetchedForClaim.current || !hasMissingConversation);

  // Conversation id -> its consultation (a ready one wins if a conversation has several).
  const consultationByThread = useMemo(() => {
    const map = new Map();
    for (const c of consultations) {
      for (const k of consultationKeys(c)) {
        const prev = map.get(k);
        if (!prev || (prev.advice?.status !== 'ready' && c.advice?.status === 'ready')) map.set(k, c);
      }
    }
    return map;
  }, [consultations]);

  // ?email=<address> (links from imavenai.com and the advice-ready email, also through the old
  // /consultations route): an address other than the verified login one opens the find box.
  const queryEmail = useMemo(() => {
    const e = (new URLSearchParams(location.search).get('email') || '').trim();
    return EMAIL_RE.test(e) ? e : '';
  }, [location.search]);
  const loginEmail = consult?.email || user?.email || '';
  const loginVerified = !!consult?.emailVerified;
  const queryNeedsClaim = !!consult && !!queryEmail
    && !(loginVerified && queryEmail.toLowerCase() === loginEmail.toLowerCase());
  useEffect(() => { if (queryNeedsClaim) setShowFind(true); }, [queryNeedsClaim, queryEmail]);

  // Pre-fill: the link's email first; otherwise the login email unless the server already checked it.
  const findPrefill = queryNeedsClaim ? queryEmail : (loginVerified ? '' : loginEmail);
  const findHint = loginVerified && loginEmail
    ? `We already checked ${loginEmail}, the email you signed in with. Used a different email on imavenai.com? Enter it below and we will send a 6-digit code to it.`
    : undefined;

  const onConsultationsClaimed = (n, email) => {
    if (n > 0) {
      setClaimBanner(`${n} ${n === 1 ? 'consultation was' : 'consultations were'} added from ${email}.`);
      setShowFind(false);
      refetchedForClaim.current = true; // the explicit refresh below replaces the one-time refetch
      loadConsultations();
      fetchExperts({ silent: true });
    }
  };

  // Add listener for expert online status updates
  useEffect(() => {
    const handleExpertOnlineStatus = (data) => {
      setExperts(prevExperts =>
        prevExperts.map(expert => ({
          ...expert,
          isOnline: data[expert.userId] ?? false
        }))
      );
    };

    // Add the listener
    const unsubscribe = onExpertOnlineStatusReceived(handleExpertOnlineStatus);

    // Cleanup the listener when component unmounts
    return () => {
      unsubscribe && unsubscribe();
    };
  }, [experts]);

  useEffect(() => {
    // Handle expertIdParam if provided
    if (expertIdParam && experts.length > 0) {
      const matchingExpert = experts.find(expert => expert.id === expertIdParam);
      if (matchingExpert) {
        handleScheduleMeeting(matchingExpert);
      }
    }
  }, [experts, expertIdParam]);

  const handleScheduleMeeting = async (expert) => {
    // Never open the calendar for an AI advisor (deep links included); see ExpertCard
    const isPureAI = expert.advisorType === 'ai'
      || (!expert.advisorType && String(expert.userId || '').startsWith('template|'));
    if (isPureAI || expert.canScheduleMeeting === false) return;
    setExpertId(expert.id)
    // An AI agent's meetings are with its owner
    setExpertName(expert.meetingHost?.name || expert.name)
    setIsModal(true)
  };

  const getMeetingInfo = useCallback((expertId) => {
    const expert = experts.find((expert) => expert.id === expertId);
    return expert ? expert.meetingInfo : null;
  }, [experts]);

  const loadExpertsAgain = useCallback(async (flag) => {
    try {
      // Always close the modal first
      setIsModal(false);

      // Wait a bit before doing anything else
      await new Promise(resolve => setTimeout(resolve, 100));

      // Then fetch new data
      setIsLoading(true);
      await fetchExperts();

      // If we need to reopen the modal, do it after another small delay
      if (flag) {
        setTimeout(() => {
          setIsModal(true);
        }, 200);
      }
    } catch (error) {
      console.error('Error fetching experts:', error);
    } finally {
      setIsLoading(false);
    }
  }, [fetchExperts]);

  // Ensure safe cleanup of dialog
  const handleDialogClose = useCallback(() => {
    // Use setTimeout to ensure the state update happens
    // after the current execution context
    setTimeout(() => {
      setIsModal(false);
    }, 0);
  }, []);

  const animationSettings = { effect: 'None' };
  const buttons = [
    {
      click: () => {
        setIsModal(false);
      },
      buttonModel: {
        isPrimary: false,
        content: 'Close',
        cssClass: 'close-button'
      },
    },
  ];

  const dialogKey = `dialog-${expertId}-${isModel}`;

  const CalendarContent = useMemo(() => {
    const currentExpert = experts.find((expert) => expert.id === expertId);
    return {
      template: () => (
        <WeeklyCalendar
          key={`calendar-${expertId}`}
          expertId={currentExpert?.meetingHost?.id || expertId}
          meetingInfo={currentExpert?.meetingInfo}
          expertName={expertName}
          isMettingScheduled={currentExpert?.isMettingScheduled}
          finishSettingMeeting={loadExpertsAgain}
          threadId={currentExpert?.threadId}
          closeWindow={handleDialogClose}
        />
      )
    };
  }, [experts, expertId, expertName, loadExpertsAgain, setIsModal, handleDialogClose]);

  // Add a function to calculate responsive dialog width based on screen size
  const getDialogWidth = () => {
    if (window.innerWidth <= 480) {
      return '100%';
    } else if (window.innerWidth <= 768) {
      return '90%';
    } else if (window.innerWidth <= 1024) {
      return '80%';
    } else {
      return '60%';
    }
  };

  const getDialogHeight = () => {
    if (window.innerWidth <= 768) {
      return '100%';
    } else {
      return 'auto';
    }
  };

  return (
    <ExpertBoardContainer ref={targetElement}>
      {isLoading ? (
        <div style={{ position: 'relative', width: '100%', height: '500px', background: 'linear-gradient(180deg, #f9fbff 0%, #f5f8fd 100%)' }}>
          <ImavenLoadingScreen message="Loading your conversations" />
        </div>
      ) : (
        <>
          <div>
            <DialogComponent
              key={dialogKey}
              id="modalDialog"
              isModal={true}
              buttons={buttons}
              header={experts.find((expert) => expert.id === expertId)?.isMettingScheduled ?
                "Update Meeting" : "Set Up a Meeting"}
              width={getDialogWidth()}
              height={getDialogHeight()}
              content={CalendarContent}
              ref={dialogInstance}
              target={targetElement.current}
              visible={isModel}
              open={() => setIsModal(true)}
              close={() => setIsModal(false)}
              animationSettings={animationSettings}
              cssClass="responsive-dialog"
            />
          </div>

          {(experts.length > 0 || unmatchedConsultations.length > 0) && !showFind && (
            <HomeTopRow>
              <QuietLink type="button" onClick={() => setShowFind(true)}>Find consultations from another email</QuietLink>
            </HomeTopRow>
          )}

          {claimBanner && (
            <HomeBanner role="status">
              <span>{claimBanner}</span>
              <QuietLink type="button" onClick={() => setClaimBanner(null)}>Dismiss</QuietLink>
            </HomeBanner>
          )}

          {/* Nothing on Home yet: explain where imavenai.com consultations come from and offer the box. */}
          {experts.length === 0 && consult && unmatchedConsultations.length === 0 && looseSettled && (
            <>
              <EmptyIntro>
                No conversations yet. If you asked for advice on imavenai.com, find it with the email you gave there.
              </EmptyIntro>
              <FindConsultationsBox initialEmail={findPrefill} hint={findHint} onClaimed={onConsultationsClaimed} />
            </>
          )}

          {showFind && (experts.length > 0 || unmatchedConsultations.length > 0) && (
            <FindConsultationsBox
              title="Find consultations from another email"
              initialEmail={findPrefill}
              hint={findHint}
              onClaimed={onConsultationsClaimed}
              onCancel={() => setShowFind(false)}
            />
          )}

          <ExpertCards>
            {experts.map((expert, index) => (
              <ExpertCardComponent
                key={`${expert.id}-${index}`}
                expert={expert}
                onScheduleMeeting={handleScheduleMeeting}
                isMettingScheduled={expert.isMettingScheduled}
                consultation={consultationByThread.get(String(expert.threadId || ''))}
                onReadConsultation={setReading}
              />
            ))}
            {/* Consultations without a conversation card (no conversation linked to them yet). */}
            {looseSettled && unmatchedConsultations.map((c) => (
              <LooseCard key={c.engagementId} data-testid="loose-consultation">
                <div className="name">{c.advisorName || (c.kind === 'ai' ? 'AI advisor' : 'Adviser')}</div>
                <div className="meta">
                  {[c.categoryLabel, CONSULT_STATUS[c.advice?.status] || (c.kind === 'ai' ? 'Intake in progress' : 'With your adviser')].filter(Boolean).join(' · ')}
                </div>
                {c.needText && <div className="need">“{c.needText}”</div>}
                {c.advice?.status === 'locked' && <div className="note">Unlock it on imavenai.com (the link is in your email).</div>}
                {c.advice?.status === 'ready' && (
                  <Button variant={ButtonVariant.SCHEDULE_OUTLINE} size={ButtonSize.MD} onClick={() => setReading(c)}>
                    Read consultation
                  </Button>
                )}
              </LooseCard>
            ))}
          </ExpertCards>
        </>
      )}

      {reading && (
        <ConsultationAdviceModal
          engagementId={reading.engagementId}
          advisorName={reading.advisorName}
          onClose={() => setReading(null)}
        />
      )}
    </ExpertBoardContainer>
  );
};

export default ExpertBoard;
